if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.autoLoadsUsed = 0;
      }

      connectedCallback() {
        this.grid = document.getElementById(this.dataset.gridId);
        this.button = this.querySelector('[data-infinite-scroll-button]');
        this.spinner = this.querySelector('[data-infinite-scroll-spinner]');
        this.status = this.querySelector('[data-infinite-scroll-status]');
        this.endMessage = this.querySelector('[data-infinite-scroll-end]');
        this.errorMessage = this.querySelector('[data-infinite-scroll-error]');

        this.nextUrl = this.dataset.nextUrl || '';
        this.autoLoadLimit = parseInt(this.dataset.autoLoads, 10);
        if (Number.isNaN(this.autoLoadLimit)) this.autoLoadLimit = 3;

        if (this.button) {
          this.onButtonClick = this.onButtonClick.bind(this);
          this.button.addEventListener('click', this.onButtonClick);
        }

        if (!this.grid || !this.nextUrl) {
          this.reachedEnd();
          return;
        }

        this.startObserving();
      }

      disconnectedCallback() {
        this.stopObserving();
        if (this.button) this.button.removeEventListener('click', this.onButtonClick);
      }

      startObserving() {
        if (typeof IntersectionObserver === 'undefined') {
          // No observer support: the button stays a plain link to the next page.
          this.showButton();
          return;
        }

        // Rendered visible so it works without JS; auto-loading takes over here.
        this.hideButton();

        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting || this.loading || !this.nextUrl) return;

            if (this.autoLoadsUsed >= this.autoLoadLimit) {
              // Pause auto-loading so the shopper can still reach the footer.
              this.stopObserving();
              this.showButton();
              return;
            }

            this.autoLoadsUsed += 1;
            this.load();
          },
          { rootMargin: '600px 0px' }
        );

        /* Observe the element itself. An empty sentinel div gets caught by
           base.css's `div:empty { display: none }` rule, has zero size, and can
           never intersect. This element always has padding, so it has layout. */
        this.observer.observe(this);
      }

      stopObserving() {
        if (!this.observer) return;
        this.observer.disconnect();
        this.observer = null;
      }

      /* Re-arm the observer after appending. IntersectionObserver only fires on a
         change, so if this element is still on screen it would otherwise go quiet. */
      recheck() {
        if (!this.observer) return;
        this.observer.unobserve(this);
        this.observer.observe(this);
      }

      onButtonClick(event) {
        // Without JS this is a normal link to the next page; with JS we load in place.
        if (!this.nextUrl) return;
        event.preventDefault();
        this.autoLoadsUsed = 0;
        this.hideButton();
        this.load().then(() => {
          if (this.nextUrl) this.startObserving();
        });
      }

      async load() {
        if (this.loading || !this.nextUrl) return;

        this.loading = true;
        this.hideError();
        this.showSpinner();

        try {
          const url = new URL(this.nextUrl, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`Request failed: ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const newGrid = doc.getElementById(this.dataset.gridId);
          if (!newGrid) throw new Error('Product grid missing from response');

          const fragment = document.createDocumentFragment();
          const added = Array.from(newGrid.children);
          added.forEach((item) => fragment.appendChild(item));
          this.grid.appendChild(fragment);

          // Keep Dawn's reveal-on-scroll animations working for appended cards.
          if (typeof initializeScrollAnimationTrigger === 'function') {
            added.forEach((item) => initializeScrollAnimationTrigger(item));
          }

          this.nextUrl = doc.querySelector('infinite-scroll')?.dataset.nextUrl || '';
          if (this.button) this.button.href = this.nextUrl || '#';

          this.announce();

          if (!this.nextUrl) {
            this.stopObserving();
            this.reachedEnd();
          } else {
            this.recheck();
          }
        } catch (error) {
          console.error('[infinite-scroll]', error);
          this.stopObserving();
          this.showError();
          this.showButton();
        } finally {
          this.loading = false;
          this.hideSpinner();
        }
      }

      announce() {
        if (!this.status) return;
        const shown = this.grid.querySelectorAll('.grid__item').length;
        this.status.textContent = `Showing ${shown} products`;
      }

      reachedEnd() {
        this.hideButton();
        if (this.endMessage && this.grid) this.endMessage.hidden = false;
      }

      showButton() {
        if (this.button) this.button.hidden = false;
      }

      hideButton() {
        if (this.button) this.button.hidden = true;
      }

      showSpinner() {
        if (this.spinner) this.spinner.hidden = false;
      }

      hideSpinner() {
        if (this.spinner) this.spinner.hidden = true;
      }

      showError() {
        if (this.errorMessage) this.errorMessage.hidden = false;
      }

      hideError() {
        if (this.errorMessage) this.errorMessage.hidden = true;
      }
    }
  );
}
